"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.viewportSpace = exports.letterSpacing = exports.layoutSpacers = exports.headingSizes = exports.fixedSpace = exports.baseStyles = void 0;
var _core = require("@linaria/core");
var _breakpoints = require("./breakpoints.js");
var _base = require("./base.functions");
var _intents = _interopRequireDefault(require("./intents.js"));
/* eslint-disable max-len */

/**
 * Fixed Space object
 *
 * @example
 * ```
 * import { fixedSpace } from './base.style.js'
 *
 * const style = css`
 *  margin: ${fixedSpace['100']};
 * `
 * ```
 * or the preferred method:
 * ```
 * const style = css`
 *  margin: var(--fixed-space-100);
 * `
 * ```
 */
const fixedSpace = exports.fixedSpace = {
  '-400': '0.25rem',
  // 4px
  '-300': '0.5rem',
  // 8px
  '-200': '0.75rem',
  // 12px
  '-100': '0.875rem',
  // 14px
  '100': '1rem',
  // 16px
  '150': '1.25rem',
  // 20px
  '200': '1.5rem',
  // 24px
  '250': '1.75rem',
  // 28px
  '300': '2rem',
  // 32px
  '400': '2.5rem',
  // 40px
  '500': '3rem',
  // 48px
  '600': '3.5rem',
  // 56px
  '700': '4.5rem',
  // 72px
  '800': '6rem',
  // 96px
  '900': '7.5rem',
  // 120px
  '1000': '10rem' // 160px
};

/**
 * Viewport Space object
 *
 * @example
 * ```
 * import { viewportSpace } from './base.style.js'
 *
 * const style = css`
 *  margin: ${viewportSpace['100']};
 * `
 * ```
 * or the preferred method:
 * ```
 * const style = css`
 *  margin: var(--viewport-space-100);
 * `
 * ```
 */
const viewportSpace = exports.viewportSpace = {
  '-400': '2vw',
  '-300': '4vw',
  '-200': '6vw',
  '4': '0.4vw',
  '100': '8vw',
  '200': '16vw',
  '300': '32vw',
  '400': '48vw',
  '500': '64vw'
};

/**
 * Layout Spacers object
 *
 * @example
 * ```
 * import { layoutSpacers } from './base.style.js'
 *
 * const style = css`
 *  margin: ${layoutSpacers['vert-base']} ${layoutSpacers['rail-base']};
 * `
 * ```
 * or the preferred method:
 * ```
 * const style = css`
 *  margin: var(--layout-vert-base) var(--layout-rail-base);
 * `
 * ```
 */
const layoutSpacers = exports.layoutSpacers = {
  'rail-base': fixedSpace['100'],
  'rail-tabletLg': viewportSpace['-300'],
  // future rail spacing viewportSpace['100']
  'rail-desktopLg': viewportSpace['-300'],
  // future rail spacing viewportSpace['100']
  'vert-base': fixedSpace['100'],
  'vert-tabletLg': viewportSpace['100'],
  'vert-desktop': fixedSpace['300'],
  'vert-desktopLg': viewportSpace['-200']
};

/**
 * Heading Sizes object
 *
 * @example
 * ```
 * import { headingSizes } from './base.style.js'
 *
 * const style = css`
 *  font-size: ${headingSizes['d1']};
 * `
 * ```
 * or the preferred method:
 * ```
 * const style = css`
 *  font-size: var(--heading-d1);
 * `
 * ```
 */
const headingSizes = exports.headingSizes = {
  // Display
  d1: `${(0, _base.clampBuilder)((0, _base.ms)(8), (0, _base.ms)(16))}`,
  // from: 2.566rem (41.06px), to: 6.583rem (105.33px)
  d2: `${(0, _base.clampBuilder)((0, _base.ms)(7), (0, _base.ms)(14))}`,
  // from: 2.281rem (36.50px), to: 5.202rem (83.23px)
  d3: `${(0, _base.clampBuilder)((0, _base.ms)(6), (0, _base.ms)(12))}`,
  // from: 2.027rem (32.43px), to: 4.110rem (65.76px)

  // Headline
  h1: `${(0, _base.clampBuilder)((0, _base.ms)(6), (0, _base.ms)(10))}`,
  // from: 2.027rem (32.43px), to: 3.247rem (51.95px)
  h2: `${(0, _base.clampBuilder)((0, _base.ms)(5), (0, _base.ms)(9))}`,
  // from: 1.802rem (28.83px), to: 2.877rem (46.03px)
  h3: `${(0, _base.clampBuilder)((0, _base.ms)(4), (0, _base.ms)(8))}`,
  // from: 1.602rem (25.63px), to: 2.566rem (41.06px)
  h4: `${(0, _base.clampBuilder)((0, _base.ms)(3), (0, _base.ms)(7))}`,
  // from: 1.424rem (22.78px), to: 2.281rem (36.50px)
  h5: `${(0, _base.clampBuilder)((0, _base.ms)(2), (0, _base.ms)(6))}`,
  // from: 1.266rem (20.26px), to: 2.027rem (32.43px)
  h6: `${(0, _base.clampBuilder)((0, _base.ms)(1), (0, _base.ms)(5))}` // from: 1.125rem (18.00px), to: 1.802rem (28.83px)
};

/**
 * Letter Spacing object
 *
 * @example
 * ```
 * import { letterSpacing } from './base.style.js'
 *
 * const style = css`
 *  letter-spacing: ${letterSpacing['d1']};
 * `
 * ```
 * or the preferred method:
 * ```
 * const style = css`
 *  letter-spacing: var(--letter-spacing-d1);
 * `
 * ```
 */
const letterSpacing = exports.letterSpacing = {
  d1: `${(0, _base.clampBuilder)(0.01, 0.15)}`,
  d2: `${(0, _base.clampBuilder)(0.01, 0.15)}`,
  d3: `${(0, _base.clampBuilder)(0.01, 0.1)}`
};

/* UxCore Title Font Size Variables */
const TEXT_SCALE_FACTOR = 1.125;
const textSizes = {
  fontSize0: _intents.default.ux.text.fontSize,
  // 16px
  fontSize1: `calc(var(--text-fontSize0) * ${TEXT_SCALE_FACTOR})`,
  // 18px
  fontSize2: `calc(var(--text-fontSize1) * ${TEXT_SCALE_FACTOR})`,
  // 20.25px
  fontSize3: `calc(var(--text-fontSize2) * ${TEXT_SCALE_FACTOR})`,
  // 22.78px
  fontSize4: `calc(var(--text-fontSize3) * ${TEXT_SCALE_FACTOR})`,
  // 25.62px
  fontSize5: `calc(var(--text-fontSize4) * ${TEXT_SCALE_FACTOR})`,
  // 28.83px
  fontSize6: `calc(var(--text-fontSize5) * ${TEXT_SCALE_FACTOR})` // 32.43px
};
const baseStyles = exports.baseStyles = "utils-b18zkeif";